import { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { useLang } from '../i18n/LanguageContext';

const fmt     = n => Number(n || 0).toLocaleString();
const fmtDate = ts => ts ? new Date(ts).toLocaleDateString('uz-UZ', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '—';
const timeSince = (ts, t) => {
  if (!ts) return '—';
  const m = Math.floor((Date.now() - new Date(ts)) / 60000);
  if (m < 60) return `${m} min oldin`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} soat oldin`;
  return `${Math.floor(h / 24)} kun oldin`;
};

function getStatus(t) {
  return {
    open:        { label: t('statusOpen'),       color: 'var(--red)' },
    in_progress: { label: t('statusInProgress'), color: 'var(--orange)' },
    resolved:    { label: t('statusResolved'),   color: 'var(--green)' },
  };
}

function Tab({ active, onClick, label }) {
  return (
    <button onClick={onClick} style={{
      padding: '9px 22px', borderRadius: 10, fontSize: 13, cursor: 'pointer', fontWeight: 600,
      background: active ? 'rgba(0,212,255,0.12)' : 'transparent',
      border: `1px solid ${active ? 'var(--cyan)' : 'var(--border)'}`,
      color: active ? 'var(--cyan)' : 'var(--text2)',
    }}>{label}</button>
  );
}

const Inp = ({ label, type, val, onChange, opts = {} }) => (
  <div style={{ marginBottom: 14 }}>
    <label style={{ display: 'block', fontSize: 12, color: 'var(--text2)', marginBottom: 5 }}>{label}</label>
    <input type={type} value={val} onChange={e => onChange(e.target.value)} {...opts}
      style={{ width: '100%', background: 'var(--card)', border: '1px solid var(--border)',
        color: 'var(--text)', padding: '9px 13px', borderRadius: 9, fontSize: 13,
        outline: 'none', boxSizing: 'border-box' }} />
  </div>
);

function MuammolarTab() {
  const { t } = useLang();
  const [issues, setIssues] = useState([]);
  const [stats, setStats] = useState(null);
  const [computers, setComputers] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ computer_id: '', issue: '', technician: '', cost: '', notes: '' });
  const [loading, setLoading] = useState(true);

  const STATUS = getStatus(t);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter ? `?status=${statusFilter}` : '';
      const [ir, sr] = await Promise.all([
        api.get(`/maintenance${params}`),
        api.get('/maintenance/stats'),
      ]);
      setIssues(ir.data);
      setStats(sr.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get('/owner/floor').then(r => {
      const data = r.data?.computers || r.data || [];
      setComputers(Array.isArray(data) ? data : []);
    }).catch(() => {});
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleCreate = async () => {
    if (!form.issue) return alert(t('missingIssueDesc'));
    try {
      await api.post('/maintenance', form);
      setShowForm(false);
      setForm({ computer_id: '', issue: '', technician: '', cost: '', notes: '' });
      load();
    } catch (e) { alert(e.response?.data?.error || t('error')); }
  };

  const updateStatus = async (id, status) => {
    try { await api.put(`/maintenance/${id}`, { status }); load(); }
    catch (e) { alert(t('error')); }
  };

  const statusBadge = s => {
    const st = STATUS[s] || { label: s, color: 'var(--text2)' };
    return (
      <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 20, fontWeight: 600,
        background: st.color + '18', color: st.color, border: `1px solid ${st.color}40` }}>
        {st.label}
      </span>
    );
  };

  return (
    <div>
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
          {[
            { icon: '🔴', val: stats.open || 0,        label: t('openIssues'),    color: 'var(--red)' },
            { icon: '🟡', val: stats.in_progress || 0, label: t('inProgressLabel'), color: 'var(--orange)' },
            { icon: '✅', val: stats.resolved_month || 0, label: t('resolvedMonth'), color: 'var(--green)' },
            { icon: '💸', val: fmt(stats.total_cost) + " " + t('currency'), label: t('totalCost'), color: 'var(--purple)' },
          ].map(c => (
            <div key={c.label} style={{ background: 'var(--card2)', border: '1px solid var(--border)', borderRadius: 14, padding: '18px 20px' }}>
              <div style={{ fontSize: 22, marginBottom: 6 }}>{c.icon}</div>
              <div style={{ fontFamily: 'Orbitron, sans-serif', fontSize: 18, fontWeight: 700, color: c.color }}>{c.val}</div>
              <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2 }}>{c.label}</div>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', gap: 6 }}>
          {[['', t('filterAll')], ['open', t('filterOpen')], ['in_progress', t('filterInProgress')], ['resolved', t('filterResolved')]].map(([v, l]) => (
            <button key={v} onClick={() => setStatusFilter(v)} style={{
              padding: '7px 14px', borderRadius: 8, fontSize: 12, cursor: 'pointer',
              background: statusFilter === v ? 'rgba(0,212,255,0.12)' : 'var(--card)',
              border: `1px solid ${statusFilter === v ? 'var(--cyan)' : 'var(--border)'}`,
              color: statusFilter === v ? 'var(--cyan)' : 'var(--text2)',
            }}>{l}</button>
          ))}
        </div>
        {!showForm && (
          <button onClick={() => setShowForm(true)} style={{
            background: 'rgba(0,255,136,0.1)', border: '1px solid var(--green)',
            color: 'var(--green)', padding: '9px 20px', borderRadius: 9, cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
            {t('addIssue')}
          </button>
        )}
      </div>

      {showForm && (
        <div style={{ background: 'var(--card2)', border: '1px solid var(--border)', borderRadius: 14, padding: 24, marginBottom: 20 }}>
          <h3 style={{ margin: '0 0 18px', fontSize: 15 }}>{t('newIssueTitle')}</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 12, color: 'var(--text2)', marginBottom: 5 }}>{t('issueComputer')}</label>
              <select value={form.computer_id} onChange={e => set('computer_id', e.target.value)}
                style={{ width: '100%', background: 'var(--card)', border: '1px solid var(--border)',
                  color: 'var(--text)', padding: '9px 13px', borderRadius: 9, fontSize: 13, outline: 'none' }}>
                <option value="">{t('selectComputerPlaceholder')}</option>
                {computers.map(c => <option key={c.id} value={c.id}>PC #{c.number} ({c.type})</option>)}
              </select>
            </div>
            <Inp label={t('issueTechnician')} type="text" val={form.technician} onChange={v => set('technician', v)} opts={{ placeholder: t('technicianPlaceholder') }} />
            <Inp label={t('issueCost')} type="number" val={form.cost} onChange={v => set('cost', v)} opts={{ placeholder: '0', min: '0' }} />
            <Inp label={t('issueNotes')} type="text" val={form.notes} onChange={v => set('notes', v)} opts={{ placeholder: t('optionalPlaceholder') }} />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 12, color: 'var(--text2)', marginBottom: 5 }}>{t('issueDesc')}</label>
            <textarea value={form.issue} onChange={e => set('issue', e.target.value)} placeholder={t('issuePlaceholder')} rows={2}
              style={{ width: '100%', background: 'var(--card)', border: '1px solid var(--border)',
                color: 'var(--text)', padding: '9px 13px', borderRadius: 9, fontSize: 13, outline: 'none',
                boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }} />
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => setShowForm(false)} style={{ flex: 1, background: 'transparent',
              border: '1px solid var(--border)', color: 'var(--text2)', padding: '9px', borderRadius: 8, cursor: 'pointer', fontSize: 12 }}>
              {t('cancel')}
            </button>
            <button onClick={handleCreate} style={{ flex: 2, background: 'rgba(0,255,136,0.12)',
              border: '1px solid var(--green)', color: 'var(--green)', padding: '9px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
              💾 {t('save')}
            </button>
          </div>
        </div>
      )}

      <div style={{ background: 'var(--card2)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text3)' }}>{t('loading')}</div>
        ) : issues.length === 0 ? (
          <div style={{ padding: 60, textAlign: 'center' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
            <div style={{ fontSize: 15, color: 'var(--text2)' }}>{t('noIssues')}</div>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--card)' }}>
                {[t('colPC'), t('colIssue'), t('colStatus'), t('colTechnician'), t('colCostHdr'), t('colDate'), t('colActions')].map(h => (
                  <th key={h} style={{ padding: '12px 16px', textAlign: 'left', color: 'var(--text2)', fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {issues.map(iss => (
                <tr key={iss.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '12px 16px', fontWeight: 600 }}>{iss.computer_number ? `#${iss.computer_number}` : '—'}</td>
                  <td style={{ padding: '12px 16px', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {iss.issue}
                  </td>
                  <td style={{ padding: '12px 16px' }}>{statusBadge(iss.status)}</td>
                  <td style={{ padding: '12px 16px', color: 'var(--text2)' }}>{iss.technician || '—'}</td>
                  <td style={{ padding: '12px 16px', color: iss.cost > 0 ? 'var(--red)' : 'var(--text3)' }}>
                    {iss.cost > 0 ? fmt(iss.cost) + ' ' + t('currency') : '—'}
                  </td>
                  <td style={{ padding: '12px 16px', color: 'var(--text3)', fontSize: 11 }}>
                    {fmtDate(iss.created_at)}<br/>{timeSince(iss.created_at, t)}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <select value={iss.status}
                        onChange={e => updateStatus(iss.id, e.target.value)}
                        style={{ background: 'var(--card)', border: '1px solid var(--border)', color: 'var(--text)',
                          padding: '5px 8px', borderRadius: 7, fontSize: 11, outline: 'none', cursor: 'pointer' }}>
                        <option value="open">{t('statusOpen')}</option>
                        <option value="in_progress">{t('statusInProgress')}</option>
                        <option value="resolved">{t('statusResolved')}</option>
                      </select>
                      {iss.status !== 'resolved' && (
                        <button onClick={() => updateStatus(iss.id, 'resolved')} style={{
                          background: 'rgba(0,255,136,0.1)', border: '1px solid var(--green)',
                          color: 'var(--green)', padding: '5px 10px', borderRadius: 7, cursor: 'pointer', fontSize: 11 }}>
                          ✓
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function SpecsTab() {
  const { t } = useLang();
  const [computers, setComputers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ gpu: '', ram: '', storage: '' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/owner/floor').then(r => {
      const data = r.data?.computers || r.data || [];
      setComputers(Array.isArray(data) ? data : []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const openEdit = c => {
    setEditing(c.id);
    setEditForm({ gpu: c.gpu || '', ram: c.ram || '', storage: c.storage || '' });
  };

  const saveSpecs = async (id) => {
    try {
      await api.put(`/maintenance/computer/${id}/specs`, editForm);
      setEditing(null);
      const r = await api.get('/owner/floor');
      const data = r.data?.computers || r.data || [];
      setComputers(Array.isArray(data) ? data : []);
    } catch (e) { alert(e.response?.data?.error || t('error')); }
  };

  return (
    <div style={{ background: 'var(--card2)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text3)' }}>{t('loading')}</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: 'var(--card)' }}>
              {[t('colPC'), t('specsType'), t('specsGPU'), t('specsRAM'), t('specsStorage'), t('specsCondition'), t('colActions')].map(h => (
                <th key={h} style={{ padding: '12px 16px', textAlign: 'left', color: 'var(--text2)', fontWeight: 600 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {computers.map(c => (
              <tr key={c.id} style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={{ padding: '12px 16px', fontWeight: 700 }}>#{c.number}</td>
                <td style={{ padding: '12px 16px', color: 'var(--text2)' }}>{c.type}</td>
                {editing === c.id ? (
                  <>
                    <td style={{ padding: '8px 12px' }}>
                      <input value={editForm.gpu} onChange={e => setEditForm(f => ({ ...f, gpu: e.target.value }))}
                        placeholder="GPU..." style={{ width: '100%', background: 'var(--card)', border: '1px solid var(--border)',
                          color: 'var(--text)', padding: '6px 10px', borderRadius: 7, fontSize: 12, outline: 'none', boxSizing: 'border-box' }} />
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <input value={editForm.ram} onChange={e => setEditForm(f => ({ ...f, ram: e.target.value }))}
                        placeholder="RAM..." style={{ width: '100%', background: 'var(--card)', border: '1px solid var(--border)',
                          color: 'var(--text)', padding: '6px 10px', borderRadius: 7, fontSize: 12, outline: 'none', boxSizing: 'border-box' }} />
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <input value={editForm.storage} onChange={e => setEditForm(f => ({ ...f, storage: e.target.value }))}
                        placeholder="..." style={{ width: '100%', background: 'var(--card)', border: '1px solid var(--border)',
                          color: 'var(--text)', padding: '6px 10px', borderRadius: 7, fontSize: 12, outline: 'none', boxSizing: 'border-box' }} />
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      {c.is_broken ? (
                        <span style={{ fontSize: 11, padding: '3px 9px', borderRadius: 20,
                          background: 'rgba(255,59,92,0.12)', color: 'var(--red)', border: '1px solid rgba(255,59,92,0.3)' }}>{t('specsBrokenShort')}</span>
                      ) : (
                        <span style={{ fontSize: 11, padding: '3px 9px', borderRadius: 20,
                          background: 'rgba(0,255,136,0.08)', color: 'var(--green)', border: '1px solid rgba(0,255,136,0.2)' }}>{t('specsGoodShort')}</span>
                      )}
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={() => saveSpecs(c.id)} style={{
                          background: 'rgba(0,255,136,0.1)', border: '1px solid var(--green)',
                          color: 'var(--green)', padding: '5px 12px', borderRadius: 7, cursor: 'pointer', fontSize: 12 }}>💾</button>
                        <button onClick={() => setEditing(null)} style={{
                          background: 'transparent', border: '1px solid var(--border)', color: 'var(--text2)',
                          padding: '5px 10px', borderRadius: 7, cursor: 'pointer', fontSize: 12 }}>✕</button>
                      </div>
                    </td>
                  </>
                ) : (
                  <>
                    <td style={{ padding: '12px 16px', color: 'var(--text2)' }}>{c.gpu || '—'}</td>
                    <td style={{ padding: '12px 16px', color: 'var(--text2)' }}>{c.ram || '—'}</td>
                    <td style={{ padding: '12px 16px', color: 'var(--text2)' }}>{c.storage || '—'}</td>
                    <td style={{ padding: '12px 16px' }}>
                      {c.is_broken ? (
                        <span style={{ fontSize: 11, padding: '3px 9px', borderRadius: 20,
                          background: 'rgba(255,59,92,0.12)', color: 'var(--red)', border: '1px solid rgba(255,59,92,0.3)' }}>{t('specsBroken')}</span>
                      ) : (
                        <span style={{ fontSize: 11, padding: '3px 9px', borderRadius: 20,
                          background: 'rgba(0,255,136,0.08)', color: 'var(--green)', border: '1px solid rgba(0,255,136,0.2)' }}>{t('specsGood')}</span>
                      )}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <button onClick={() => openEdit(c)} style={{
                        background: 'transparent', border: '1px solid var(--border)', color: 'var(--text2)',
                        padding: '5px 12px', borderRadius: 7, cursor: 'pointer', fontSize: 12 }}>✏️</button>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default function Maintenance() {
  const { t } = useLang();
  const [tab, setTab] = useState('issues');
  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>{t('maintenanceTitle')}</h1>
        <div style={{ fontSize: 13, color: 'var(--text2)' }}>{t('maintenanceSubtitle')}</div>
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 28 }}>
        <Tab active={tab === 'issues'} onClick={() => setTab('issues')} label={t('mainTabIssues')} />
        <Tab active={tab === 'specs'}  onClick={() => setTab('specs')}  label={t('mainTabSpecs')} />
      </div>
      {tab === 'issues' && <MuammolarTab />}
      {tab === 'specs'  && <SpecsTab />}
    </div>
  );
}
